import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Clock3, Vote } from 'lucide-react';
import Alert from '../components/common/Alert';
import Badge from '../components/common/Badge';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Input from '../components/forms/Input';
import Select from '../components/forms/Select';
import Table from '../components/tables/Table';
import { electionDayService } from '../services';

function ElectionDayPage() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [canMark, setCanMark] = useState(false);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [listResponse, summaryResponse] = await Promise.all([
        electionDayService.getAll({ status, search, page_size: 100 }),
        electionDayService.getSummary(),
      ]);
      setRows(listResponse.data);
      setCanMark(Boolean(listResponse.meta?.can_mark));
      setSummary(summaryResponse.data);
    } catch (requestError) {
      setError(requestError.message || 'No fue posible cargar la planilla del Día D.');
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (row) => {
    setSavingId(row.voter_id);
    setError('');
    try {
      await electionDayService.setStatus(row.voter_id, !row.has_voted);
      await load();
    } catch (requestError) {
      setError(requestError.message || 'No fue posible actualizar el estado electoral.');
    } finally {
      setSavingId(null);
    }
  };

  const columns = [
    { key: 'document', header: 'Cédula' },
    { key: 'voter_name', header: 'Votante', render: (value) => <span className="font-semibold text-content-primary">{value}</span> },
    { key: 'phone', header: 'Teléfono' }, { key: 'leader_name', header: 'Puntero' },
    { key: 'voting_location', header: 'Local' }, { key: 'voting_table', header: 'Mesa' },
    { key: 'has_voted', header: 'Estado', render: (value) => value
      ? <Badge variant="success"><CheckCircle2 className="size-3.5" /> Ya votó</Badge>
      : <Badge variant="warning"><Clock3 className="size-3.5" /> Aún no votó</Badge> },
    { key: 'actions', header: 'Acción', render: (_, row) => canMark && (
      <Button variant={row.has_voted ? 'outline' : 'primary'} size="sm" loading={savingId === row.voter_id} onClick={() => toggle(row)}>
        <Vote className="size-4" /> {row.has_voted ? 'Corregir: aún no' : 'Marcar que votó'}
      </Button>
    ) },
  ];

  return <div className="space-y-6">
    <div><p className="text-sm font-semibold text-primary-700">Operativo electoral</p><h1 className="mt-1 text-2xl font-bold">Planilla Día D</h1><p className="mt-1 text-sm text-content-muted">Marca una sola vez quién ya votó y consulta quién continúa pendiente.</p></div>
    {error && <Alert variant="danger" title="No se pudo completar" onClose={() => setError('')}>{error}</Alert>}
    {summary && <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Card><p className="text-sm text-content-muted">Total</p><p className="mt-2 text-3xl font-bold">{summary.total}</p></Card>
      <Card><p className="text-sm text-content-muted">Ya votaron</p><p className="mt-2 text-3xl font-bold text-success-700">{summary.voted}</p></Card>
      <Card><p className="text-sm text-content-muted">Aún no votaron</p><p className="mt-2 text-3xl font-bold text-warning-700">{summary.pending}</p></Card>
      <Card><p className="text-sm text-content-muted">Participación</p><p className="mt-2 text-3xl font-bold text-primary-700">{summary.percentage}%</p></Card>
    </div>}
    {!canMark && <Alert variant="info" title="Modo consulta">Tu rol puede consultar el avance, pero las marcas corresponden a punteros y responsables de móviles aprobados.</Alert>}
    <Card title="Control de votación" description="Una marca existente se actualiza; nunca se crea un registro duplicado.">
      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        <Input id="election-search" label="Buscar" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Nombre, cédula o teléfono" />
        <Select id="election-status" label="Estado" value={status} onChange={(event) => setStatus(event.target.value)} placeholder="Todos" options={[{ value: 'VOTED', label: 'Ya votaron' }, { value: 'PENDING', label: 'Aún no votaron' }]} />
      </div>
      {loading ? <div className="grid min-h-52 place-items-center"><LoadingSpinner label="Cargando planilla" /></div> : <Table columns={columns} data={rows} rowKey="voter_id" emptyMessage="No hay votantes para los filtros seleccionados." />}
    </Card>
  </div>;
}

export default ElectionDayPage;
